import React, { useState, useEffect } from "react";
import {
  OuterBox,
  LoadingButton,
  SubheaderSection,
  MandatoryNote,
  SelectField,
  CommonTextField,
} from "./../../../components";
import { Grid } from "@mui/material";
import EventCard from "./EventCard";
import DocumentUpload from "./../DocumentUpload";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { useAlert, useAxios, useComponent } from "../../../hooks/";
import pages from "./../../../constants/pages";
import { useNavigate } from "react-router-dom";
import { Loader } from "../../../components";

const MyComponent = () => {
  const [eventOptions, setEventOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stateOptions, setStateOptions] = useState([]);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [eventObject, setEventObject] = useState([]);
  const axios = useAxios();
  const { alert } = useAlert();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loader } = useComponent();

  const onSubmit = async (values) => {
    try {
      setLoading(true);
      const requestData = {
        visitor_id: sessionStorage.getItem("visitorId"),
        email_id: values.email,
        event_id: values.event,
        state_id: values.state,
        district_id: values.district,
      };

      if (values.documentFile) {
        requestData.visitor_card = values.documentFile;
      }

      if (values.organization) {
        requestData.organization_name = values.organization;
      }

      if (values.designation) {
        requestData.designation = values.designation;
      }

      const response = await axios({
        url: "/booking/appointment/book-event",
        method: "POST",
        data: requestData,
      });

      if (response.status) {
        setLoading(false);
        if (!response.data.id) {
          dispatch(
            alert({ type: "error", message: response.message.displayMessage })
          );
        } else {
          dispatch(
            alert({ type: "success", message: response.message.displayMessage })
          );
          sessionStorage.setItem("id", response.data.id);
          navigate(pages.EventBookingDetails.route, {
            state: { details: response.data },
          });
        }
      }
    } catch (error) {
      console.error("Error in fetching department list", error);
    }
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email("Invalid email format")
      .matches(
        /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
        "Invalid email format"
      )
      .required("Email is required"),
    event: Yup.string().required("Event is required"),
    state: Yup.string().required("State is required"),
    district: Yup.string().required("District is required"),
    organization: Yup.string(),
    designation: Yup.string(),
    documentFile: Yup.mixed(),
  });

  const formik = useFormik({
    initialValues: {
      email: sessionStorage.getItem("email") || "",
      event: "",
      state: "",
      district: "",
      organization: "",
      designation: "",
      documentFile: "",
    },
    validationSchema,
    onSubmit,
  });

  useEffect(() => {
    loader.start(2);
    axios({
      url: "/admin/appointments/states",
      method: "GET",
    })
      .then((response) => {
        if (response.status) {
          if (response.data.length > 0) {
            const formattedState = response.data.map((state) => ({
              id: state.state_id,
              name: state.state_name,
            }));
            setStateOptions(formattedState);
          } else {
            dispatch(
              alert({ type: "error", message: response.message.displayMessage })
            );
          }
        }
        loader.apiComplete();
      })
      .catch((error) => {
        console.error("Error in fetching states list", error);
      });

    const event_id = sessionStorage.getItem("event_id");
    let URL;
    if (event_id) {
      URL = `admin/events?event_id=${event_id}`;
    } else {
      URL = "/admin/events/?only_enabled=true&registration_open=true";
    }

    axios({
      url: URL,
      method: "GET",
    })
      .then((response) => {
        if (response.status) {
          if (
            response.data.length > 0 ||
            Object.keys(response.data).length > 0
          ) {
            if (event_id) {
              setEventOptions([response.data]);
              formik.setFieldValue("event", response.data.id);
              setEventObject(response.data);
            } else {
              setEventOptions(response.data);
            }
          } else {
            dispatch(
              alert({ type: "error", message: response.message.displayMessage })
            );
          }
          loader.apiComplete();
        }
      })
      .catch((error) => {
        console.error("Error in fetching events list", error);
      });
  }, [loader]);

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    const regex = /^[A-Za-z\s]*$/;

    if (regex.test(inputValue)) {
      formik.handleChange(e);
    }
  };

  const handleDownloadPDF = (link) => {
    const pdfLink = link;
    window.open(pdfLink, "_blank");
  };

  const handleEventSelect = (event) => {
    const selectedEventId = event.target.value;
    formik.setFieldValue("event", selectedEventId);
    const selectedEvent = eventOptions.find(
      (event) => event.id === parseInt(selectedEventId)
    );
    setEventObject(selectedEvent);
  };

  const handleStateChange = (event) => {
    setDistrictOptions([]);
    const selectedState = event.target.value;
    formik.setFieldValue("state", selectedState);
    formik.setFieldValue("district", "");
    fetchDistricts(selectedState);
  };

  const fetchDistricts = async (selectedState) => {
    try {
      const response = await axios({
        url: `/admin/appointments/districts?state_id=${selectedState}`,
        method: "GET",
      });
      if (response.status) {
        const formattedDistricts = response.data.map((district) => ({
          id: district.district_id,
          name: district.district_name,
        }));
        setDistrictOptions(formattedDistricts);
      }
    } catch (error) {
      console.error("Error in fetching district list", error);
    }
  };

  return (
    <Loader>
      <OuterBox>
        <SubheaderSection
          textAlign="left"
          title="Book Event"
          content="Please provide the following details to Join the Event"
        />

        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2} sx={{ textTransform: "capitalize" }}>
            <CommonTextField
              label="Enter Your Email"
              placeholder="Enter Your Email"
              name="email"
              type="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              required
            />

            <SelectField
              label="Select Event"
              name="event"
              value={formik.values.event}
              options={eventOptions}
              onChange={handleEventSelect}
              onBlur={formik.handleBlur}
              error={formik.touched.event && formik.errors.event}
              required
            />

            {eventObject.name && (
              <Grid item xs={12} sm={12}>
                <EventCard
                  eventObject={eventObject}
                  handleDownloadPDF={handleDownloadPDF}
                />
              </Grid>
            )}

            <SelectField
              label="Choose Your State"
              value={formik.values.state}
              onChange={handleStateChange}
              options={stateOptions}
              error={formik.touched.state && Boolean(formik.errors.state)}
              required
            />

            <SelectField
              label="Choose Your District"
              name="district"
              value={formik.values.district}
              options={districtOptions}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.district && formik.errors.district}
              required
            />

            <CommonTextField
              label="Enter Your organization's Name(Optional)"
              placeholder="Enter Your organization's Name(Optional)"
              name="organization"
              type="text"
              value={formik.values.organization}
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
            />
            <CommonTextField
              label="Enter Your Designation(Optional)"
              placeholder="Enter Your Designation Name(Optional)"
              name="designation"
              type="text"
              value={formik.values.designation}
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
            />

            <DocumentUpload
              setUploadLoading={setUploadLoading}
              uploadLoading={uploadLoading}
              formik={formik}
            />
          </Grid>

          <LoadingButton
            loading={loading}
            onClick={formik.handleSubmit}
            disabled={uploadLoading}
            fullWidth
          >
            Submit
          </LoadingButton>

          <MandatoryNote />
        </form>
      </OuterBox>
    </Loader>
  );
};

export default MyComponent;
